'use client';

import { logger } from '@curology/logger/client';
import React, { useMemo } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { Button } from '@/components/squeeze';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { type HttpError } from '@/utils/http/client';
import type { ErrorBag } from '@/utils/http/types';

import { useSelectedItemIds } from '../../../hooks/useSelectedItemIds';
import { useSubmittingForm } from '../../../hooks/useSubmittingForm';

// Fail-fast policy for checkout (no payment processing). Ideally a shared constant / http policy.
const FAIL_FAST_TIMEOUT_MS = 10_000;

const CHECKOUT_UPDATE_ERROR_MESSAGE =
  'Failed to update selected items during checkout';

function isHttpError(exception: unknown): exception is HttpError<ErrorBag> {
  return (
    typeof exception === 'object' &&
    exception !== null &&
    'message' in exception &&
    typeof (exception as { message?: unknown }).message === 'string'
  );
}

function isAbortError(exception: unknown): boolean {
  if (typeof exception !== 'object' || exception === null) return false;
  const e = exception as { code?: string; name?: string };
  return e.code === 'ERR_CANCELED' || e.name === 'AbortError';
}

function handleCheckoutError({
  exception,
  showError,
  context,
}: {
  exception: unknown;
  showError: (params: { title: string; message: string }) => void;
  context: {
    brand: string;
    treatmentCategory: string;
    selectedItemIds: number[];
  };
}): void {
  if (isHttpError(exception)) {
    logger.error(CHECKOUT_UPDATE_ERROR_MESSAGE, {
      error: exception.message,
      code: exception.code,
      status: exception.response?.status,
      statusText: exception.response?.statusText,
      responseData: exception.response?.data,
      ...context,
    });

    if (!exception.response) {
      const isTimeoutOrAbort =
        isAbortError(exception) ||
        exception.code === 'ECONNABORTED' ||
        (typeof exception.message === 'string' &&
          exception.message.includes('timeout'));

      showError({
        title: 'Sorry!',
        message: isTimeoutOrAbort
          ? 'Request timeout. Please check your connection and try again.'
          : 'Network connection lost. Please check your connection and try again.',
      });
      return;
    }

    const responseData = exception.response?.data as
      | { message?: string }
      | undefined;
    const errorMessage =
      responseData?.message ??
      'An error occurred while updating your selection. Please try again.';

    showError({
      title: 'Sorry!',
      message: errorMessage,
    });
  } else if (isAbortError(exception)) {
    logger.error(CHECKOUT_UPDATE_ERROR_MESSAGE, {
      error: 'Request aborted',
      ...context,
    });
    showError({
      title: 'Sorry!',
      message: 'Request timeout. Please check your connection and try again.',
    });
  } else {
    logger.error(CHECKOUT_UPDATE_ERROR_MESSAGE, {
      error: String(exception),
      ...context,
    });
    showError({
      title: 'Sorry!',
      message:
        'An error occurred while updating your selection. Please try again.',
    });
  }
}

const CheckOutNowButton = ({
  className,
  navigateToCheckout,
}: {
  className?: string;
  navigateToCheckout: () => void;
}) => {
  const { brand, treatmentCategory } = useFlowContext();
  const { selectedItemIds, selectedRxCards } = useSelectedItemIds();
  const { submittingForm, setSubmittingForm } = useSubmittingForm();
  const { error: showError } = useFlashMessages();

  const isButtonDisabled = useMemo(
    () => submittingForm || selectedRxCards.length === 0,
    [submittingForm, selectedRxCards],
  );

  return (
    <div className={className}>
      <Button
        className="cur-w-full"
        data-sentry-id="Check out now"
        loading={submittingForm}
        disabled={isButtonDisabled}
        onClick={async () => {
          setSubmittingForm(true);

          trackEvent(AnalyticsEvent.PROCEED_TO_CHECKOUT_BUTTON_CLICKED);

          const controller = new AbortController();
          const timeoutId = window.setTimeout(
            () => controller.abort(),
            FAIL_FAST_TIMEOUT_MS,
          );

          try {
            await ProductSelectionAndCheckoutService.updateSelectedItems(
              brand,
              selectedItemIds,
              treatmentCategory,
              { signal: controller.signal },
            );

            navigateToCheckout();
            // Spinner stays on until navigation completes; button stays disabled
          } catch (exception) {
            setSubmittingForm(false);
            handleCheckoutError({
              exception,
              showError,
              context: { brand, treatmentCategory, selectedItemIds },
            });
          } finally {
            window.clearTimeout(timeoutId);
          }
        }}
        size="lg"
      >
        {selectedRxCards.length > 0
          ? 'Check out now'
          : 'Select at least 1 rx product to continue'}
      </Button>
    </div>
  );
};

export default CheckOutNowButton;
